import { useCallback, useState } from "react";
import { devicesApi } from "@/api/services";
import { requestPushToken, clearPushToken, pushSupported } from "@/lib/push";
import { scopedStorageKey } from "@/lib/storageScope";

const DEVICE_ID_KEY = "pushDeviceId";

function storedDeviceId(): string | null {
  try { return localStorage.getItem(scopedStorageKey(DEVICE_ID_KEY)); } catch { return null; }
}
function setStoredDeviceId(id: string | null): void {
  try {
    const k = scopedStorageKey(DEVICE_ID_KEY);
    if (id) localStorage.setItem(k, id); else localStorage.removeItem(k);
  } catch { /* storage unavailable */ }
}

export function usePushToken() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Request permission + token and register into customer_devices.
   * Returns true iff a token was registered. */
  const enablePush = useCallback(async (): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      const token = await requestPushToken();
      if (!token) {
        setError("Push not available or permission denied");
        return false;
      }
      const { deviceId } = await devicesApi.register(token, "web");
      setStoredDeviceId(deviceId);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to enable push");
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  /** Remove the registered device + clear local token. */
  const disablePush = useCallback(async (): Promise<void> => {
    setBusy(true);
    try {
      const id = storedDeviceId();
      if (id) await devicesApi.remove(id).catch(() => undefined);
      await clearPushToken();
      setStoredDeviceId(null);
    } finally {
      setBusy(false);
    }
  }, []);

  return { enablePush, disablePush, busy, error, pushSupported };
}
